import { notificationLabels } from "@/i18n";
import type { Language } from "@/utils/languageUtils";
import { buildAuctionListingPath } from "@/lib/navigation/notificationNavigation";
import { resolveSupportNotificationCategoryLabel } from "@/utils/supportCategoryLabel";

const auctionListingRoute = (data: Record<string, unknown>) =>
  buildAuctionListingPath(data) ?? "/auctions";

const listingUrlOrAuctionRoute = (data: Record<string, unknown>) => {
  const url = data.listingUrl ?? data.listing_url ?? data.link;
  if (typeof url === "string" && url.trim()) return url.trim();
  return auctionListingRoute(data);
};

type NotificationUiStatus = "action_required" | "completed" | "info";

interface NotificationTemplate {
  title: string | ((data: Record<string, unknown>) => string);
  description: (data: Record<string, unknown>) => string;
  status:
    | NotificationUiStatus
    | ((data: Record<string, unknown>) => NotificationUiStatus);
  action?: {
    label: string | ((data: Record<string, unknown>) => string);
    route: string | ((data: Record<string, unknown>) => string);
    isDownload?: boolean; // Flag to indicate this is a download action
  };
}

export const resolveNotificationTitle = (
  template: NotificationTemplate,
  data: Record<string, unknown> = {},
) => (typeof template.title === "function" ? template.title(data) : template.title);

export const resolveNotificationStatus = (
  template: NotificationTemplate,
  data: Record<string, unknown> = {},
): NotificationUiStatus =>
  typeof template.status === "function" ? template.status(data) : template.status;

/**
 * Helper function to replace placeholders in template strings
 */
const replacePlaceholders = (
  template: string,
  data: Record<string, unknown>
): string => {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = data[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
    return match; // Return the placeholder if no value found
  });
};

const withVehicleFields = (data: Record<string, unknown>) => {
  const carModel =
    data.carModel ||
    data.listingTitle ||
    data.vehicleName ||
    data.regNumber ||
    data.registrationNo;
  const bidAmount =
    data.bidAmount != null
      ? Number(data.bidAmount).toLocaleString("sv-SE")
      : data.newHighestBid != null
        ? Number(data.newHighestBid).toLocaleString("sv-SE")
        : data.bidAmount;
  return { ...data, carModel, bidAmount };
};

function formatNotificationTimeRemaining(
  endTime: unknown,
  lang?: Language,
): string {
  if (endTime == null || endTime === "") return "";
  const end = new Date(String(endTime));
  if (Number.isNaN(end.getTime())) return "";

  const diffMs = end.getTime() - Date.now();
  const isEn = lang === "en";

  if (diffMs <= 0) {
    return isEn ? "soon" : "snart";
  }

  const totalMinutes = Math.ceil(diffMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    if (isEn) {
      return hours > 0 ? `${days} d ${hours} h` : `${days} day${days === 1 ? "" : "s"}`;
    }
    return hours > 0 ? `${days} d ${hours} h` : `${days} dag${days === 1 ? "" : "ar"}`;
  }
  if (hours > 0) {
    if (minutes > 0) {
      return isEn ? `${hours} h ${minutes} min` : `${hours} tim ${minutes} min`;
    }
    return isEn ? `${hours} hour${hours === 1 ? "" : "s"}` : `${hours} timme${hours === 1 ? "" : "r"}`;
  }
  return isEn
    ? `${minutes} minute${minutes === 1 ? "" : "s"}`
    : `${minutes} minut${minutes === 1 ? "" : "er"}`;
}

export const getNotificationTemplate = (
  type: string,
  lang?: Language
): NotificationTemplate | null => {
  const notificationLabels_i18n = notificationLabels(lang);
  const templates = notificationLabels_i18n.templates || {};
  const d = (notificationLabels_i18n.defaults || {}) as Record<string, string>;

  const getTemplate = (key: string) => templates[key] || templates.default;
  const templateMap: Record<string, () => NotificationTemplate> = {
    new_bid_own_car: () => {
      const t = getTemplate("newBidOwnCar");
      return {
        title: t.title,
        description: (data) => {
          const fields = withVehicleFields(data);
          const reserveRaw =
            data.reservePrice ?? data.reservationPrice ?? data.reserve;
          const reserveNum =
            reserveRaw != null && reserveRaw !== ""
              ? Number(reserveRaw)
              : NaN;
          const bidNum =
            data.newHighestBid != null
              ? Number(data.newHighestBid)
              : data.bidAmount != null
                ? Number(data.bidAmount)
                : NaN;
          let reservePart = "";
          if (Number.isFinite(reserveNum) && reserveNum > 0) {
            const reserveFmt = reserveNum.toLocaleString("sv-SE");
            if (Number.isFinite(bidNum) && bidNum >= reserveNum) {
              reservePart =
                lang === "en"
                  ? ` Reserve price (${reserveFmt} SEK) has been reached.`
                  : ` Reservationspriset (${reserveFmt} kr) är uppnått.`;
            } else {
              reservePart =
                lang === "en"
                  ? ` Reserve price: ${reserveFmt} SEK.`
                  : ` Reservationspris: ${reserveFmt} kr.`;
            }
          }
          return replacePlaceholders(t.description, {
            ...fields,
            reservePart,
          });
        },
        status: "info",
        action: {
          label: t.actionLabel,
          route: `/my-auctions`,
        },
      };
    },
    new_bid_other_car: () => {
      const t = getTemplate("newBidOtherCar");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "info",
        action: {
          label: t.actionLabel,
          route: `/auctions`,
        },
      };
    },
    test_approved: () => {
      const t = getTemplate("testApproved");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "completed",
        action: {
          label: t.actionLabel,
          route: `/car-test?tab=submitted`,
        },
      };
    },
    documentation_reminder: () => {
      const t = getTemplate("documentationReminder");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: `/my-auctions`,
        },
      };
    },
    system_update: () => {
      const t = getTemplate("systemUpdate");
      return {
        title: t.title,
        description: () => t.description,
        status: "info",
      };
    },
    new_auction_started: () => {
      const t = getTemplate("newAuctionStarted");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: `/my-auctions`,
        },
      };
    },
    listing_reserved: () => {
      const t = getTemplate("listingReserved");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, withVehicleFields(data)),
        status: "info",
        action: {
          label: t.actionLabel,
          route: listingUrlOrAuctionRoute,
        },
      };
    },
    listing_ended: () => {
      const t = getTemplate("listingEnded");
      return {
        title: t.title,
        description: (data) => {
          const listingTitle =
            (data.listingTitle as string) || d.unknownListing;
          const hasWinner = data.hasWinner === true;
          const winnerUsername = data.winnerUsername
            ? String(data.winnerUsername)
            : "";
          const winningBidAmount =
            data.winningBidAmount != null
              ? Number(data.winningBidAmount).toLocaleString("sv-SE")
              : "";
          const totalBids =
            data.totalBids != null ? String(data.totalBids) : "";
          const isEn = lang === "en";
          const winnerPart = hasWinner
            ? winnerUsername
              ? isEn
                ? ` — winner: ${winnerUsername}`
                : ` — vinnare: ${winnerUsername}`
              : isEn
                ? " — winner assigned"
                : " — vinnare utsedd"
            : isEn
              ? " — no winner"
              : " — ingen vinnare";
          const bidPart = winningBidAmount
            ? isEn
              ? `, winning bid ${winningBidAmount} kr`
              : `, vinnande bud ${winningBidAmount} kr`
            : "";
          const bidsPart = totalBids
            ? isEn
              ? ` (${totalBids} bids)`
              : ` (${totalBids} bud)`
            : "";
          return replacePlaceholders(t.description, {
            listingTitle,
            winnerPart,
            bidPart,
            bidsPart,
          });
        },
        status: "info",
        action: {
          label: t.actionLabel,
          route: listingUrlOrAuctionRoute,
        },
      };
    },
    listing_sold: () => {
      const t = getTemplate("listingSold");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, withVehicleFields(data)),
        status: "completed",
        action: {
          label: t.actionLabel,
          route: listingUrlOrAuctionRoute,
        },
      };
    },
    favorite_car_sold: () => {
      const t = getTemplate("favoriteCarSold");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "info",
        action: {
          label: t.actionLabel,
          route: `/favorites`,
        },
      };
    },
    order_placed: () => {
      const t = getTemplate("orderPlaced");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "completed",
        action: {
          label: t.actionLabel,
          route: `/orders`,
        },
      };
    },
    order_shipped: () => {
      const t = getTemplate("orderShipped");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "completed",
        action: {
          label: t.actionLabel,
          route: (data) => `/orders/${data.orderId || ""}`,
        },
      };
    },
    auction_ending: () => {
      const t = getTemplate("auctionEnding");
      return {
        title: t.title,
        description: (data) => {
          const listingTitle =
            (data.listingTitle as string) ||
            (data.regNumber as string) ||
            d.unknownListing ||
            "auktionen";
          const timeRemaining =
            formatNotificationTimeRemaining(
              data.endTime ?? data.end_time,
              lang,
            ) || (lang === "en" ? "soon" : "snart");
          return replacePlaceholders(t.description, {
            ...withVehicleFields(data),
            listingTitle,
            timeRemaining,
          });
        },
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: auctionListingRoute,
        },
      };
    },
    bid_placed: () => {
      const t = getTemplate("bidPlaced");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "info",
        action: {
          label: t.actionLabel,
          route: auctionListingRoute,
        },
      };
    },
    bid_outbid: () => {
      const t = getTemplate("bidOutbid");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            listingTitle:
              data.listingTitle || data.regNumber || d.unknownListing || "auktionen",
            newHighestBid:
              data.newHighestBid != null
                ? Number(data.newHighestBid).toLocaleString("sv-SE")
                : data.newHighestBid,
          }),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: auctionListingRoute,
        },
      };
    },
    auto_bid_limit_reached: () => {
      const t = getTemplate("autoBidLimitReached");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            listingTitle:
              data.listingTitle || data.regNumber || d.unknownListing || "auktionen",
            maxAmount:
              data.maxAmount != null
                ? Number(data.maxAmount).toLocaleString("sv-SE")
                : data.maxAmount,
            newHighestBid:
              data.newHighestBid != null
                ? Number(data.newHighestBid).toLocaleString("sv-SE")
                : data.newHighestBid,
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: auctionListingRoute,
        },
      };
    },
    registration_application_received: () => {
      const t = getTemplate("registrationApplicationReceived");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            {
              applicantUsername: data.applicantUsername || "",
              userType: data.userType === "dealer" ? d.dealer : data.userType === "business" ? d.business : (data.userType as string) || "",
            }
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: `/settings?tab=customers&view=requests`,
        },
      };
    },
    new_car_test_submitted: () => {
      const t = getTemplate("newCarTestSubmitted");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            {
              vehicleName: data.vehicleName || d.unknownVehicle,
              regNumber: data.regNumber || "N/A",
            }
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: `/admin/car-test`,
        },
      };
    },
    car_test_withdrawn_from_review: () => {
      const t = getTemplate("carTestWithdrawnFromReview");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            username: data.username || d.unknownUser || "Användare",
            vehicleName: data.vehicleName || d.unknownVehicle,
            regNumber: data.regNumber || "N/A",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: `/admin/car-test`,
        },
      };
    },
    car_test_approved: () => {
      const t = getTemplate("carTestApproved");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            {
              regNumber: data.regNumber || "N/A",
            }
          ),
        status: "completed",
        action: {
          label: t.actionLabel,
          route: `/car-test?tab=submitted`,
        },
      };
    },
    car_test_rejected: () => {
      const t = getTemplate("carTestRejected");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            {
              regNumber: data.regNumber || "N/A",
            }
          ),
        status: "info",
        action: {
          label: t.actionLabel,
          route: `/car-test?tab=submitted`,
        },
      };
    },
    car_test_complement_required: () => {
      const t = getTemplate("carTestComplementRequired");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            {
              regNumber: data.regNumber || "N/A",
              complementMessage: data.complementMessage || d.complementPlease,
            }
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: `/car-test`,
        },
      };
    },
    car_test_resubmitted_after_complement: () => {
      const t = getTemplate("carTestResubmittedAfterComplement");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            {
              regNumber: data.regNumber || "N/A",
              complementMessage: data.complementMessage || d.complementRequested,
            }
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: `/admin/car-test`,
        },
      };
    },
    dealer_mentioned_in_journey_comment: () => {
      const t = getTemplate("dealerMentionedInJourneyComment");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            mentionedByName: data.mentionedByName || d.colleague,
            contextTitle: data.contextTitle || d.journey,
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: (data) => (data.contextUrl && typeof data.contextUrl === "string" ? data.contextUrl : "/dealer-portal"),
        },
      };
    },
    dealer_journey_assigned: () => {
      const t = getTemplate("dealerJourneyAssigned");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            assignedByName: data.assignedByName || d.colleague,
            contextTitle: data.contextTitle || d.journey,
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: (data) => (data.contextUrl && typeof data.contextUrl === "string" ? data.contextUrl : "/dealer-portal"),
        },
      };
    },
    dealer_journey_update: () => {
      const t = getTemplate("dealerJourneyUpdate");
      const defaultsRecord = d as Record<string, string>;
      return {
        title: t.title,
        description: (data) => {
          const contextTitle =
            data.contextTitle ||
            [data.customerName, data.regNr].filter(Boolean).join(" ").trim() ||
            d.journey;
          const changeDescription =
            (data.changeDescriptionKey && defaultsRecord[data.changeDescriptionKey as string]) ||
            data.changeDescription ||
            d.update;
          return replacePlaceholders(t.description, {
            changeDescription,
            contextTitle,
          });
        },
        status: "info",
        action: {
          label: t.actionLabel,
          route: (data) => (data.contextUrl && typeof data.contextUrl === "string" ? data.contextUrl : "/dealer-portal"),
        },
      };
    },
    admin_mentioned_in_comment: () => {
      const t = getTemplate("adminMentionedInComment");
      return {
        title: t.title,
        description: (data) => {
          const contextType =
            data.contextType === "lead"
              ? d.lead
              : data.contextType === "bugReport"
                ? d.bugReport
                : d.vehicleCard;
          const defaultTitle =
            data.contextType === "lead" ? d.lead : data.contextType === "bugReport" ? d.bugReport : d.vehicleCard;
          const contextTitle = data.contextTitle || defaultTitle;
          const formattedContext =
            data.contextType === "lead"
              ? `${d.lead} ${contextTitle}`
              : data.contextType === "bugReport"
                ? `${d.bugReport} ${contextTitle}`
                : `${d.vehicleCard} ${contextTitle}`;
          return replacePlaceholders(t.description, {
            mentionedByName: data.mentionedByName || d.colleague,
            contextTitle: formattedContext,
            contextType,
          });
        },
        status: "info",
        action: {
          label: (data) => {
            const contextType =
              data.contextType === "lead" ? d.lead : data.contextType === "bugReport" ? d.bugReport : d.vehicleCard;
            return replacePlaceholders(t.actionLabel, { contextType });
          },
          route: (data) => {
            if (data.contextUrl && typeof data.contextUrl === "string") {
              return data.contextUrl;
            }
            if (data.contextType === "lead" && data.contextId && typeof data.contextId === "string") {
              return `/admin/leads/${data.contextId}`;
            }
            if (data.contextType === "vehicleCard" && data.contextId && typeof data.contextId === "string") {
              return `/admin/vehicle-cards/${data.contextId}`;
            }
            if (data.contextType === "bugReport" && data.contextId && typeof data.contextId === "string") {
              return `/admin/feedback?tab=bugs&id=${data.contextId}`;
            }
            return "/admin";
          },
        },
      };
    },
    buy_now_attempt: () => {
      const t = getTemplate("buyNowAttempt");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(
            t.description,
            withVehicleFields(data),
          ),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: (data) => {
            const id = data.listingId || data.listing_id;
            const qs = new URLSearchParams({
              tab: "listings",
              status: "reserved",
            });
            if (id) qs.set("listingId", String(id));
            return `/admin/leadbox/listings?${qs.toString()}`;
          },
        },
      };
    },
    contract_sent: () => {
      const t = getTemplate("contractSent");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
            contractNumber: data.contractNumber || "N/A",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: (data) => `/api/v1/notifications/${data.notificationId || ""}/download`,
          isDownload: true,
        },
      };
    },
    contract_signature_requested: () => {
      const t = getTemplate("contractSignatureRequested");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
            contractNumber: data.contractNumber || "N/A",
          }),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: () => "/orders",
        },
      };
    },
    seller_contract_sent: () => {
      const t = getTemplate("sellerContractSent");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
            contractNumber: data.contractNumber || "N/A",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: (data) => `/api/v1/notifications/${data.notificationId || ""}/download`,
          isDownload: true,
        },
      };
    },
    seller_contract_signature_requested: () => {
      const t = getTemplate("sellerContractSignatureRequested");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
            contractNumber: data.contractNumber || "N/A",
          }),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: () => "/my-auctions",
        },
      };
    },
    seller_contract_signed: () => {
      const t = getTemplate("sellerContractSigned");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            contractNumber: data.contractNumber || "N/A",
            signerName: data.signerName || "N/A",
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: () => "/admin/economy",
        },
      };
    },
    contract_signed_by_buyer: () => {
      const t = getTemplate("contractSignedByBuyer");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            contractNumber: data.contractNumber || "N/A",
            signerName: data.signerName || d.unknownBuyer,
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: () => "/admin/economy",
        },
      };
    },
    invoice_sent: () => {
      const t = getTemplate("invoiceSent");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            regNumber: data.regNumber || "N/A",
            listingTitle: data.listingTitle ? ` (${data.listingTitle})` : "",
            invoiceNumber: data.invoiceNumber || "N/A",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: (data) =>
            (data.ordersUrl && typeof data.ordersUrl === "string"
              ? data.ordersUrl
              : "/orders"),
        },
      };
    },
    new_lead_incoming: () => {
      const t = getTemplate("newLeadIncoming");
      return {
        title: t.title,
        description: (data) => {
          const leadContact = data.leadContact || d.unknownContact;
          return replacePlaceholders(t.description, {
            leadContact,
          });
        },
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: "/admin/leadbox",
        },
      };
    },
    listing_cancelled: () => {
      const t = getTemplate("listingCancelled");
      return {
        title: t.title,
        description: (data) => {
          // Prefer i18n template with placeholders; fallback to backend message if no listingTitle
          const listingTitle =
            (data.listingTitle as string) ||
            (data.message as string) ||
            d.unknownListing;
          return replacePlaceholders(t.description, { ...data, listingTitle });
        },
        status: "info",
        action: {
          label: (data) => (data.actionLabel as string) || t.actionLabel,
          route: listingUrlOrAuctionRoute,
        },
      };
    },
    auction_extended: () => {
      const t = getTemplate("auctionExtended");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            listingTitle: data.listingTitle || d.unknownListing,
            newEndTime: data.newEndTime || "",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: listingUrlOrAuctionRoute,
        },
      };
    },
    buyer_delivery_booked: () => {
      const tAdmin = getTemplate("buyerDeliveryBooked");
      const tBuyer = getTemplate("buyerDeliveryBookedBuyer");
      const tSeller = getTemplate("buyerDeliveryBookedSeller");
      const pick = (data: Record<string, unknown>) => {
        if (data.forSeller) return tSeller;
        if (data.forBuyer) return tBuyer;
        return tAdmin;
      };
      return {
        title: (data) => pick(data).title,
        description: (data) => {
          const t = pick(data);
          const transportType = (data.transportType as string) || d.delivery;
          const regNumber = (data.regNumber as string) || "N/A";
          const listingTitle = (data.listingTitle as string) || d.unknownListing;
          const buyerName = (data.buyerName as string) || d.unknownBuyer;
          const deliveryDate = (data.deliveryDate as string) || "N/A";
          const deliveryAddress = (data.deliveryAddress as string)
            ? `\n${d.deliveryAddressLabel}: ${data.deliveryAddress}`
            : "";
          const comment = (data.comment as string)
            ? `\n${d.commentLabel}: ${data.comment}`
            : "";
          return replacePlaceholders(t.description, {
            transportType,
            regNumber,
            listingTitle,
            buyerName,
            deliveryDate,
            deliveryAddress,
            comment,
          });
        },
        status: (data) =>
          data.forBuyer || data.forSeller ? "info" : "action_required",
        action: {
          label: (data) => pick(data).actionLabel || tAdmin.actionLabel,
          route: (data) => {
            if (data.forSeller) return "/my-auctions";
            if (data.forBuyer) return "/orders";
            const id = data.listingId || data.listing_id;
            const qs = new URLSearchParams({ tab: "listings" });
            if (id) qs.set("listingId", String(id));
            return `/admin/leadbox/listings?${qs.toString()}`;
          },
        },
      };
    },
    buyer_delivery_updated: () => {
      const tBuyerWait = getTemplate("buyerDeliveryUpdatedBuyer");
      const tBuyerByAdmin = getTemplate("buyerDeliveryUpdatedBuyerByAdmin");
      const tSeller = getTemplate("buyerDeliveryUpdatedSeller");
      const tSellerPickup = getTemplate("sellerPickupUpdated");
      const tAdmin = getTemplate("buyerDeliveryUpdated");
      const pick = (data: Record<string, unknown>) => {
        if (data.forSeller && data.sellerPickup) return tSellerPickup;
        if (data.forSeller) return tSeller;
        if (data.forBuyer && data.updatedByAdmin) return tBuyerByAdmin;
        if (data.forBuyer) return tBuyerWait;
        return tAdmin;
      };
      return {
        title: (data) => pick(data).title,
        description: (data) => {
          const t = pick(data);
          const transportType = (data.transportType as string) || d.delivery;
          const regNumber = (data.regNumber as string) || "N/A";
          const listingTitle = (data.listingTitle as string) || d.unknownListing;
          const buyerName = (data.buyerName as string) || d.unknownBuyer;
          const deliveryDate = (data.deliveryDate as string) || "N/A";
          const deliveryAddress = (data.deliveryAddress as string)
            ? `\n${d.deliveryAddressLabel}: ${data.deliveryAddress}`
            : "";
          const comment = (data.comment as string)
            ? `\n${d.commentLabel}: ${data.comment}`
            : "";
          return replacePlaceholders(t.description, {
            transportType,
            regNumber,
            listingTitle,
            buyerName,
            deliveryDate,
            deliveryAddress,
            comment,
          });
        },
        status: (data) =>
          data.forBuyer || data.forSeller
            ? data.forBuyer && data.updatedByAdmin
              ? "completed"
              : "info"
            : "action_required",
        action: {
          label: (data) => pick(data).actionLabel || tAdmin.actionLabel,
          route: (data) => {
            if (data.forSeller) return "/my-auctions";
            if (data.forBuyer) return "/orders";
            const id = data.listingId || data.listing_id;
            const qs = new URLSearchParams({ tab: "listings" });
            if (id) qs.set("listingId", String(id));
            return `/admin/leadbox/listings?${qs.toString()}`;
          },
        },
      };
    },
    buyer_delivery_confirmed: () => {
      const tBuyer = getTemplate("buyerDeliveryConfirmed");
      const tSeller = getTemplate("buyerDeliveryConfirmedSeller");
      const pick = (data: Record<string, unknown>) =>
        data.forSeller ? tSeller : tBuyer;
      return {
        title: (data) => pick(data).title,
        description: (data) => {
          const t = pick(data);
          const transportType = (data.transportType as string) || d.delivery;
          const regNumber = (data.regNumber as string) || "N/A";
          const listingTitle = (data.listingTitle as string) || d.unknownListing;
          const deliveryDate = (data.deliveryDate as string) || "N/A";
          const deliveryAddress = (data.deliveryAddress as string)
            ? `\n- ${d.deliveryAddressLabel}: ${data.deliveryAddress}`
            : "";
          const comment = (data.comment as string)
            ? `\n- ${d.commentLabel}: ${data.comment}`
            : "";
          return replacePlaceholders(t.description, {
            transportType,
            regNumber,
            listingTitle,
            deliveryDate,
            deliveryAddress,
            comment,
          });
        },
        status: "completed",
        action: {
          label: (data) => pick(data).actionLabel || tBuyer.actionLabel,
          route: (data) => (data.forSeller ? "/my-auctions" : "/orders"),
        },
      };
    },
    support_case_created: () => {
      const t = getTemplate("supportCaseCreated");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            caseTypeLabel: data.caseTypeLabel || "",
            customerName: data.customerName || "",
            categoryLabel: resolveSupportNotificationCategoryLabel(data, lang),
            caseRef: data.caseRef || data.caseId || "",
          }),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: (data) =>
            data.caseId ? `/admin/support/${data.caseId}` : "/admin/support",
        },
      };
    },
    support_case_customer_activity: () => {
      const t = getTemplate("supportCaseCustomerActivity");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            activityLabel: data.activityLabel || "",
            customerName: data.customerName || "",
            caseRef: data.caseRef || data.caseId || "",
            content: data.content || "",
          }),
        status: "action_required",
        action: {
          label: t.actionLabel,
          route: (data) =>
            data.caseId ? `/admin/support/${data.caseId}` : "/admin/support",
        },
      };
    },
    support_case_admin_reply: () => {
      const t = getTemplate("supportCaseAdminReply");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            caseRef: data.caseRef || data.caseId || "",
            content: data.content || "",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: "/support",
        },
      };
    },
    support_case_status_updated: () => {
      const t = getTemplate("supportCaseStatusUpdated");
      return {
        title: t.title,
        description: (data) =>
          replacePlaceholders(t.description, {
            caseRef: data.caseRef || data.caseId || "",
            statusLabel: data.statusLabel || data.status || "",
          }),
        status: "info",
        action: {
          label: t.actionLabel,
          route: "/support",
        },
      };
    },
    default: () => {
      const t = getTemplate("default");
      return {
        title: t.title,
        description: () => t.description,
        status: "info",
      };
    },
  };

  const templateFactory = templateMap[type] || templateMap[type.toLowerCase()] || templateMap.default;
  return templateFactory ? templateFactory() : null;
};
